import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  useListMembers,
  useInviteMember,
  useUpdateMemberRole,
  useRemoveMember,
  getListMembersQueryKey,
} from '../../api/generated/generated';
import type { Member, Role } from '../../api/generated';
import { getErrorMessage } from '../../api/client';
import { PageHeader } from '../../components/common/PageHeader';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { useSnackbar } from '../../components/common/SnackbarProvider';

const ROLES = [
  { value: 'user', label: 'User' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'org_owner', label: 'Owner' },
];

export function OrgMembersPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { data: membersResponse, isLoading } = useListMembers(orgId);
  const members: Member[] | undefined =
    membersResponse?.status === 200 ? membersResponse.data : undefined;
  const inviteMember = useInviteMember({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMembersQueryKey(orgId) });
      },
    },
  });
  const updateRole = useUpdateMemberRole({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMembersQueryKey(orgId) });
      },
    },
  });
  const removeMember = useRemoveMember({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMembersQueryKey(orgId) });
      },
    },
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('user');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const canManage = user?.roles?.includes('org_owner') || user?.superAdmin;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleInvite = async () => {
    try {
      await inviteMember.mutateAsync({ orgId, data: { email: inviteEmail, role: inviteRole } });
      showSnackbar('Member invited');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('user');
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  const handleRoleChange = async (uid: string, newRole: string) => {
    try {
      await updateRole.mutateAsync({ orgId, uid, data: { role: newRole as Role } });
      showSnackbar('Role updated');
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  const handleRemove = async (uid: string) => {
    try {
      await removeMember.mutateAsync({ orgId, uid });
      showSnackbar('Member removed');
      setConfirmDelete(null);
    } catch (err) {
      showSnackbar(getErrorMessage(err), 'error');
      setConfirmDelete(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Box>
      <PageHeader
        title="Members"
        actions={
          canManage ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              Invite Member
            </Button>
          ) : undefined
        }
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joined</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {members?.map((member) => (
              <TableRow key={member.uid}>
                <TableCell>{member.display_name}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>
                  {canManage && member.uid !== user?.uid ? (
                    <Select
                      size="small"
                      value={member.role}
                      onChange={(e) => handleRoleChange(member.uid, e.target.value)}
                    >
                      {ROLES.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    ROLES.find((r) => r.value === member.role)?.label ?? member.role
                  )}
                </TableCell>
                <TableCell>{formatDate(member.joined_at)}</TableCell>
                {canManage && (
                  <TableCell align="right">
                    {member.uid !== user?.uid && (
                      <Tooltip title="Remove member">
                        <IconButton
                          color="error"
                          onClick={() => setConfirmDelete(member.uid)}
                          size="small"
                          aria-label="Remove member"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Member</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Email Address"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={inviteRole} label="Role" onChange={(e) => setInviteRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={!inviteEmail || inviteMember.isPending}
          >
            {inviteMember.isPending ? 'Inviting...' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove Member"
        message="Are you sure you want to remove this member from the organization? This action cannot be undone."
        confirmLabel="Remove"
        severity="error"
        loading={removeMember.isPending}
        onConfirm={() => confirmDelete && handleRemove(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
}
